import { useEffect, useState } from "react";

import { ApiError, fetchDqReport } from "../api";
import type { DqCheck, DqReport } from "../types";

/**
 * The ledger's self-audit (`GET /v1/evidence/dq`, ADR-024).
 *
 * Same rule as every other evidence panel here: nothing is computed in the browser and
 * nothing is hardcoded - the endpoint's verdicts are rendered as they arrive, and when
 * the report cannot be fetched the panel says "Not measured yet" instead of showing a
 * reassuring green.
 *
 * The two grades of failure are rendered differently on purpose (checks.py): FAIL means
 * the record itself is broken, WARN means an operational budget is not being met. A
 * standing WARN on `clearing` is expected in this deployment - no reviewer workstation
 * was built - and the endpoint says so in the check's own details, so the panel simply
 * shows what it was told rather than explaining the warning away.
 */

const CHECK_LABELS: Record<string, { title: string; plain: string }> = {
  completeness: {
    title: "완전성",
    plain: "요청 1건 = 판정 1건 = 감사 기록 1건. 하나라도 짝이 없으면 빨간불이에요.",
  },
  conservation: {
    title: "보존",
    plain: "들어온 요청 수와 나간 판정 수가 정확히 맞는지 — 복식부기의 대차 맞추기예요.",
  },
  timeliness: {
    title: "적시성",
    plain: "요청이 도착해서 판정이 장부에 남기까지 걸린 시간(느린 쪽 1%까지).",
  },
  clearing: {
    title: "정리",
    plain: "사람에게 넘어간 건이 처리되지 않고 얼마나 쌓여 있는지.",
  },
};

const STATUS_ICON: Record<DqCheck["status"], string> = {
  PASS: "✅",
  WARN: "⚠️",
  FAIL: "❌",
};

const AGE_LABELS: Record<string, string> = {
  under_1h: "1시간 미만",
  from_1h_to_24h: "1~24시간",
  over_24h: "24시간 초과",
};

function ms(value: unknown): string {
  return typeof value === "number" ? `${value.toFixed(1)}ms` : "—";
}

/** A 200 that is not a report (a proxy, a stub, a future schema change) must land in the
 *  "Not measured yet" branch rather than half-rendering or crashing the whole tab. */
function isReport(value: unknown): value is DqReport {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<DqReport>;
  return (
    Array.isArray(candidate.checks) &&
    typeof candidate.window_days === "number" &&
    typeof candidate.requests_total === "number" &&
    typeof candidate.decisions_total === "number" &&
    typeof candidate.summary === "object" &&
    candidate.summary !== null
  );
}

function TimelinessRow({ details }: { details: Record<string, unknown> }) {
  return (
    <table className="evidence-table">
      <thead>
        <tr>
          <th>절반은 이 안에 (p50)</th>
          <th>95%는 이 안에 (p95)</th>
          <th>가장 느린 1% 경계 (p99)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{ms(details.p50_ms)}</td>
          <td>{ms(details.p95_ms)}</td>
          <td>{ms(details.p99_ms)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function ClearingRow({ details }: { details: Record<string, unknown> }) {
  const byAge = (details.by_age ?? {}) as Record<string, number>;
  return (
    <>
      <table className="evidence-table">
        <thead>
          <tr>
            <th>미해결 검토 건의 대기 시간</th>
            <th>건수</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(AGE_LABELS).map(([key, label]) => (
            <tr key={key}>
              <td>{label}</td>
              <td>{byAge[key] ?? 0}건</td>
            </tr>
          ))}
        </tbody>
      </table>
      {typeof details.note === "string" && <p className="muted">{details.note}</p>}
    </>
  );
}

export function DataQualityPanel() {
  const [report, setReport] = useState<DqReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDqReport()
      .then((body) => {
        if (isReport(body)) {
          setReport(body);
          return;
        }
        setError("Not measured yet — 정합성 리포트 형식을 알아볼 수 없습니다.");
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError
            ? `Not measured yet — 정합성 리포트를 불러오지 못했습니다 (${err.status}).`
            : "Not measured yet — 정합성 리포트를 불러오지 못했습니다.",
        );
      });
  }, []);

  if (error) {
    return (
      <section className="card" id="try-dq">
        <h3>🧮 장부 정합성 리포트</h3>
        <p className="muted">{error}</p>
      </section>
    );
  }
  if (!report) {
    return (
      <section className="card" id="try-dq">
        <h3>🧮 장부 정합성 리포트</h3>
        <p className="muted">불러오는 중…</p>
      </section>
    );
  }

  const { summary } = report;
  return (
    <section className="card" id="try-dq">
      <h3>🧮 장부 정합성 리포트 — 이 시스템이 스스로를 대사(對査)합니다</h3>
      <p>
        은행에서 제일 중요한 질문 중 하나는 &quot;장부가 맞느냐&quot;예요. 이 리포트는{" "}
        <strong>지금 이 순간의 데이터베이스를 직접 읽어서</strong>, 판정 기록이 빠지거나
        중복되거나 쌓여 있지 않은지 스스로 검사한 결과입니다. 최근{" "}
        <strong>{report.window_days}일</strong> 기준 · 요청{" "}
        {report.requests_total.toLocaleString()}건 · 판정{" "}
        {report.decisions_total.toLocaleString()}건.
      </p>
      <p className="callout">
        통과 <strong>{summary.passed}</strong> · 주의 <strong>{summary.warned}</strong> ·{" "}
        실패 <strong>{summary.failed}</strong> — <strong>실패(❌)는 기록 자체가 깨졌다는
        뜻이고, 주의(⚠️)는 기록은 멀쩡한데 운영 목표를 못 맞췄다는 뜻</strong>이에요. 둘을
        같은 빨강으로 칠하면 빨강을 무시하게 되니까 등급을 나눴습니다.
      </p>

      <div className="dq-checks">
        {report.checks.map((check) => {
          const label = CHECK_LABELS[check.name];
          return (
            <div key={check.name} className={`dq-check dq-check--${check.status.toLowerCase()}`}>
              <p className="dq-check-head">
                <span className="dq-check-status">{STATUS_ICON[check.status]}</span>
                <strong>{label?.title ?? check.name}</strong>{" "}
                <span className="dq-check-name">{check.name}</span>
              </p>
              {label && <p className="dq-check-plain">{label.plain}</p>}
              <p className="dq-check-observed">
                관측: <strong>{check.observed}</strong>
              </p>
              <p className="muted">기준: {check.expected}</p>
              {check.name === "timeliness" && <TimelinessRow details={check.details} />}
              {check.name === "clearing" && <ClearingRow details={check.details} />}
            </div>
          );
        })}
      </div>

      <p className="muted">
        생성 시각 {report.generated_at} · 이 리포트는 <strong>읽기만 합니다</strong> —
        어떤 판정도 만들거나 고치지 않아요. 설계 근거와 폐기 조건은{" "}
        <code>docs/adr/ADR-024</code>에 있습니다.
      </p>
    </section>
  );
}
